import type { ActivityStatsResponse, LevelStatsResponse } from '@arrows/shared';
import { ProgressApi } from './api';
import {
  createConfiguredAuthClient,
  normalizeApiBase,
  type ConfiguredProgressSyncOptions,
} from './service';

/** アクティビティは全ユーザー共通の集計なので、同一セッションでは使い回す。 */
export const activityCacheMs = 5 * 60 * 1_000;

interface StatsApiPort {
  getLevelStats(level: number, clearTimeMs: number | null): Promise<LevelStatsResponse>;
  getActivityStats(): Promise<ActivityStatsResponse>;
}

/**
 * 表示のためだけの読み取り。取得できなければ常に null を返し、呼び出し側は
 * その行を出さない。失敗をユーザーへ伝える経路は持たない。
 */
export interface StatsSource {
  readonly enabled: boolean;
  getLevelStats(level: number, clearTimeMs: number): Promise<LevelStatsResponse | null>;
  getActivity(): Promise<ActivityStatsResponse | null>;
}

export interface StatsClientOptions {
  readonly api?: StatsApiPort;
  readonly now?: () => number;
}

export class StatsClient implements StatsSource {
  private readonly api?: StatsApiPort;
  private readonly now: () => number;
  private activityCache: { readonly at: number; readonly value: ActivityStatsResponse | null }
    | null = null;
  private activityRequest: Promise<ActivityStatsResponse | null> | null = null;

  constructor(options: StatsClientOptions = {}) {
    this.api = options.api;
    this.now = options.now ?? (() => Date.now());
  }

  /** API 未設定・認証なしの環境では通信自体を行わない。 */
  get enabled(): boolean {
    return this.api !== undefined;
  }

  async getLevelStats(level: number, clearTimeMs: number): Promise<LevelStatsResponse | null> {
    if (this.api === undefined) return null;
    try {
      return await this.api.getLevelStats(level, clearTimeMs);
    } catch {
      return null;
    }
  }

  async getActivity(): Promise<ActivityStatsResponse | null> {
    if (this.api === undefined) return null;
    const cached = this.activityCache;
    if (cached !== null && this.now() - cached.at < activityCacheMs) return cached.value;
    // 失敗も含めてキャッシュし、クリアのたびに再試行が積み上がるのを防ぐ。
    this.activityRequest ??= this.load().finally(() => {
      this.activityRequest = null;
    });
    return this.activityRequest;
  }

  private async load(): Promise<ActivityStatsResponse | null> {
    let value: ActivityStatsResponse | null;
    try {
      value = await this.api?.getActivityStats() ?? null;
    } catch {
      value = null;
    }
    this.activityCache = { at: this.now(), value };
    return value;
  }
}

export function createConfiguredStats(options: ConfiguredProgressSyncOptions): StatsClient {
  const apiBase = normalizeApiBase(options.apiBase);
  if (apiBase === null) return new StatsClient();
  const auth = options.authClient ?? createConfiguredAuthClient(apiBase, options);
  if (auth === undefined) return new StatsClient();
  return new StatsClient({ api: new ProgressApi(apiBase, auth, options.fetcher) });
}
